import React from "react";
import { useTranslation } from "react-i18next";

import { FormCard, FormGrid } from "../FormCard";
import { InputRow, MonthRow, SelectRow } from "../Rows";
import {
    GRID_VOLTAGE_LSP,
    getGridVoltageOptions,
    isValidGridVoltageLevel,
} from "./gridCostConstants";
import {
    decimalToInputString,
    normalizeDecimalInput,
    tariffValidFromMonthToUtc,
    tariffValidToInclusiveMonthToUtc,
    utcToTariffValidFromMonth,
    utcToTariffValidToInclusiveMonth,
    validateTariffMonthRange,
} from "./gridCostUtils";

export function emptyTariffForm(gridAreaPublicId = "") {
    return {
        gridAreaTariffPublicId: "",
        gridAreaPublicId,
        gridVoltageLevel: GRID_VOLTAGE_LSP,
        tariffName: "",
        validFromMonth: "",
        validToMonth: "",
        fixedFeeMonthlySek: "0",
        transferEnergySekPerKwh: "0",
        transferSpotAddonPercent: "0",
        effectFeeSekPerKwMonth: "0",
        subscriptionSekPerKwMonth: "0",
    };
}

export function tariffToForm(tariff) {
    return {
        gridAreaTariffPublicId: tariff?.gridAreaTariffPublicId ?? "",
        gridAreaPublicId: tariff?.gridAreaPublicId ?? "",
        gridVoltageLevel: tariff?.gridVoltageLevel || GRID_VOLTAGE_LSP,
        tariffName: tariff?.tariffName ?? "",
        validFromMonth: utcToTariffValidFromMonth(tariff?.validFromUtc),
        validToMonth: utcToTariffValidToInclusiveMonth(tariff?.validToUtc),
        fixedFeeMonthlySek:
            tariff?.fixedFeeMonthlySek != null ? decimalToInputString(tariff.fixedFeeMonthlySek) : "0",
        transferEnergySekPerKwh:
            tariff?.transferEnergySekPerKwh != null ? decimalToInputString(tariff.transferEnergySekPerKwh) : "0",
        transferSpotAddonPercent:
            tariff?.transferSpotAddonPercent != null
                ? decimalToInputString(tariff.transferSpotAddonPercent)
                : "0",
        effectFeeSekPerKwMonth:
            tariff?.effectFeeSekPerKwMonth != null ? decimalToInputString(tariff.effectFeeSekPerKwMonth) : "0",
        subscriptionSekPerKwMonth:
            tariff?.subscriptionSekPerKwMonth != null
                ? decimalToInputString(tariff.subscriptionSekPerKwMonth)
                : "0",
    };
}

export function formToTariffDto(form) {
    return {
        gridAreaTariffPublicId: form.gridAreaTariffPublicId || undefined,
        gridAreaPublicId: form.gridAreaPublicId,
        gridVoltageLevel: form.gridVoltageLevel,
        tariffName: form.tariffName.trim(),
        validFromUtc: tariffValidFromMonthToUtc(form.validFromMonth),
        validToUtc: tariffValidToInclusiveMonthToUtc(form.validToMonth),
        fixedFeeMonthlySek: Number(form.fixedFeeMonthlySek),
        transferEnergySekPerKwh: Number(form.transferEnergySekPerKwh),
        transferSpotAddonPercent: Number(form.transferSpotAddonPercent),
        effectFeeSekPerKwMonth: Number(form.effectFeeSekPerKwMonth),
        subscriptionSekPerKwMonth: Number(form.subscriptionSekPerKwMonth),
        reactiveSekPerKvar: 0,
    };
}

function normalizeTariffForCompare(tariff) {
    return {
        gridAreaTariffPublicId: tariff?.gridAreaTariffPublicId ?? "",
        gridAreaPublicId: tariff?.gridAreaPublicId ?? "",
        gridVoltageLevel: tariff?.gridVoltageLevel ?? "",
        tariffName: tariff?.tariffName ?? "",
        validFromMonth: utcToTariffValidFromMonth(tariff?.validFromUtc),
        validToMonth: utcToTariffValidToInclusiveMonth(tariff?.validToUtc),
        fixedFeeMonthlySek: Number(tariff?.fixedFeeMonthlySek ?? 0),
        transferEnergySekPerKwh: Number(tariff?.transferEnergySekPerKwh ?? 0),
        transferSpotAddonPercent: Number(tariff?.transferSpotAddonPercent ?? 0),
        effectFeeSekPerKwMonth: Number(tariff?.effectFeeSekPerKwMonth ?? 0),
        subscriptionSekPerKwMonth: Number(tariff?.subscriptionSekPerKwMonth ?? 0),
    };
}

function compareNormalizedTariffs(a, b) {
    const keyA = `${a.tariffName}-${a.validFromMonth}-${a.gridAreaTariffPublicId}`;
    const keyB = `${b.tariffName}-${b.validFromMonth}-${b.gridAreaTariffPublicId}`;
    return keyA.localeCompare(keyB);
}

export function tariffsHaveChanges(currentTariffs, originalTariffs) {
    const current = (currentTariffs || []).map(normalizeTariffForCompare).sort(compareNormalizedTariffs);
    const original = (originalTariffs || []).map(normalizeTariffForCompare).sort(compareNormalizedTariffs);
    return JSON.stringify(current) !== JSON.stringify(original);
}

export function formToLocalTariff(form, existingTariff = null) {
    const dto = formToTariffDto(form);
    const isNew = !existingTariff?.gridAreaTariffPublicId;

    return {
        ...(existingTariff || {}),
        gridAreaTariffPublicId: dto.gridAreaTariffPublicId || "",
        gridAreaPublicId: dto.gridAreaPublicId,
        gridVoltageLevel: dto.gridVoltageLevel,
        tariffName: dto.tariffName,
        validFromUtc: dto.validFromUtc,
        validToUtc: dto.validToUtc,
        fixedFeeMonthlySek: dto.fixedFeeMonthlySek,
        transferEnergySekPerKwh: dto.transferEnergySekPerKwh,
        transferSpotAddonPercent: dto.transferSpotAddonPercent,
        effectFeeSekPerKwMonth: dto.effectFeeSekPerKwMonth,
        subscriptionSekPerKwMonth: dto.subscriptionSekPerKwMonth,
        _clientKey:
            existingTariff?._clientKey ||
            existingTariff?.gridAreaTariffPublicId ||
            `new-${Date.now()}`,
        _isNew: isNew,
    };
}

export default function GridAreaTariffForm({
    form,
    setForm,
    readOnlyArea = true,
    readOnly = false,
    inModal = false,
}) {
    const { t } = useTranslation();
    const voltageOptions = getGridVoltageOptions(t);
    const onChange = (field, value) => {
        if (readOnly) {
            return;
        }
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const onDecimalChange = (field) => (value) => {
        onChange(field, normalizeDecimalInput(value));
    };

    const monthRangeErrorRaw = validateTariffMonthRange(form.validFromMonth, form.validToMonth);
    const monthRangeError = !monthRangeErrorRaw
        ? null
        : !form.validFromMonth
            ? t("gridCost.selectValidFromMonth")
            : t("gridCost.validToAfterFrom");

    const title = form.gridAreaTariffPublicId ? t("pages.editTariff") : t("pages.newTariff");

    const fields = (
            <FormGrid>
                <InputRow
                    label={t("form.tariffName")}
                    value={form.tariffName}
                    onChange={(value) => onChange("tariffName", value)}
                    disabled={readOnly}
                />
                <SelectRow
                    label={t("form.voltageLevel")}
                    value={form.gridVoltageLevel}
                    options={voltageOptions}
                    required
                    isClearable={false}
                    invalid={!isValidGridVoltageLevel(form.gridVoltageLevel)}
                    invalidMessage={t("gridCost.voltageRequired")}
                    onChange={(value) => onChange("gridVoltageLevel", value ?? "")}
                    disabled={readOnly}
                />
                <MonthRow
                    label={t("form.validFromMonth")}
                    value={form.validFromMonth}
                    disabled={readOnly}
                    onChange={(value) => onChange("validFromMonth", value)}
                />
                <MonthRow
                    label={t("form.validToMonth")}
                    tooltip={t("gridCost.validToTooltip")}
                    value={form.validToMonth}
                    disabled={readOnly}
                    invalid={!readOnly && Boolean(monthRangeError)}
                    errorMessage={!readOnly ? monthRangeError : null}
                    onChange={(value) => onChange("validToMonth", value)}
                />
                <InputRow
                    label={t("form.fixedFeeMonthly")}
                    value={form.fixedFeeMonthlySek}
                    onChange={onDecimalChange("fixedFeeMonthlySek")}
                    disabled={readOnly}
                />
                <InputRow
                    label={t("form.transferEnergy")}
                    value={form.transferEnergySekPerKwh}
                    onChange={onDecimalChange("transferEnergySekPerKwh")}
                    disabled={readOnly}
                />
                <InputRow
                    label={t("form.spotAddOnDecimal")}
                    value={form.transferSpotAddonPercent}
                    onChange={onDecimalChange("transferSpotAddonPercent")}
                    tooltip={t("gridCost.spotAddOnTooltip")}
                    disabled={readOnly}
                />
                <InputRow
                    label={t("form.capacityFeeKw")}
                    value={form.effectFeeSekPerKwMonth}
                    onChange={onDecimalChange("effectFeeSekPerKwMonth")}
                    disabled={readOnly}
                />
                <InputRow
                    label={t("form.subscriptionKw")}
                    value={form.subscriptionSekPerKwMonth}
                    onChange={onDecimalChange("subscriptionSekPerKwMonth")}
                    disabled={readOnly}
                />
                {!readOnlyArea && (
                    <InputRow
                        label={t("form.gridAreaPublicId")}
                        value={form.gridAreaPublicId}
                        onChange={(value) => onChange("gridAreaPublicId", value)}
                    />
                )}
            </FormGrid>
    );

    if (inModal) {
        return (
            <div className="flex flex-col gap-4">
                <h3 className="text-xl font-bold pr-10">{title}</h3>
                {fields}
            </div>
        );
    }

    return (
        <FormCard title={title} collapsible={false}>
            {fields}
        </FormCard>
    );
}
